import { useState } from "react";
import { ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { CashMovementType } from "@/hooks/useCashRegister";
import { useNumericKeypadField } from "@/components/numeric-keypad/useNumericKeypadField";
import { centsToFixedString, toCentsFromString } from "@/lib/currency-cents";
import { ExpenseLinkFields, type ExpenseLinkDraft } from "@/components/expenses/ExpenseLinkFields";

interface CashMovementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: CashMovementType;
  onConfirm: (
    amount: number,
    reason?: string,
    expense?: {
      title: string;
      category_id: string;
      supplier_id?: string;
      due_date?: string;
    }
  ) => Promise<void>;
}

export function CashMovementDialog({
  open,
  onOpenChange,
  type,
  onConfirm,
}: CashMovementDialogProps) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [expenseDraft, setExpenseDraft] = useState<ExpenseLinkDraft>({
    enabled: false,
    title: "",
    categoryId: "",
    supplierId: undefined,
    dueDate: undefined,
  });

  const movementAmountField = useNumericKeypadField({
    id: "cash.movement_amount",
    label: type === "withdrawal" ? "Valor da sangria" : "Valor do suprimento",
    mode: "currency",
    getCents: () => toCentsFromString(amount),
    setCents: (c) => setAmount(centsToFixedString(c)),
    maxCents: 999_999_99,
  });

  const isWithdrawal = type === "withdrawal";
  const title = isWithdrawal ? "Registrar Sangria" : "Registrar Suprimento";
  const Icon = isWithdrawal ? ArrowUpCircle : ArrowDownCircle;
  const iconColor = isWithdrawal ? "text-red-600" : "text-green-600";

  const canSubmitExpense =
    !isWithdrawal ||
    !expenseDraft.enabled ||
    (expenseDraft.title.trim().length > 0 && expenseDraft.categoryId);

  const handleConfirm = async () => {
    const value = parseFloat(amount);
    if (!value || value <= 0) return;

    if (isWithdrawal && expenseDraft.enabled && !canSubmitExpense) return;

    setIsSubmitting(true);
    try {
      await onConfirm(
        value,
        reason || undefined,
        isWithdrawal && expenseDraft.enabled
          ? {
              title: expenseDraft.title.trim(),
              category_id: expenseDraft.categoryId,
              supplier_id: expenseDraft.supplierId,
              due_date: expenseDraft.dueDate,
            }
          : undefined
      );
      setAmount("");
      setReason("");
      setExpenseDraft({ enabled: false, title: "", categoryId: "", supplierId: undefined, dueDate: undefined });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`h-5 w-5 ${iconColor}`} />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="amount">Valor *</Label>
            <Input
              id="amount"
              type="number"
              min="0.01"
              step="0.01"
              placeholder="R$ 0,00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onFocus={movementAmountField.activate}
              onClick={movementAmountField.activate}
              readOnly={movementAmountField.readOnly}
              inputMode={movementAmountField.inputMode}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Motivo</Label>
            <Textarea
              id="reason"
              placeholder={
                isWithdrawal
                  ? "Ex: Pagamento de fornecedor..."
                  : "Ex: Troco adicional..."
              }
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />
          </div>

          {isWithdrawal && (
            <div className="rounded-lg border p-3">
              <ExpenseLinkFields
                value={expenseDraft}
                onChange={(next) => {
                  // Seta um título padrão ao habilitar, para reduzir atrito
                  if (!expenseDraft.enabled && next.enabled && !next.title) {
                    setExpenseDraft({ ...next, title: reason?.trim() ? `Sangria - ${reason.trim()}` : "Sangria" });
                    return;
                  }
                  setExpenseDraft(next);
                }}
                checkboxLabel="Registrar como DESPESA (gera conta a pagar)"
                titleLabel="Título da despesa"
              />
              {expenseDraft.enabled && !canSubmitExpense && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Informe ao menos o título e a categoria.
                </p>
              )}
            </div>
          )}

          {isWithdrawal && (
            <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Sangria é a retirada de dinheiro do caixa. Certifique-se de registrar o motivo.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting || !amount || parseFloat(amount) <= 0 || !canSubmitExpense}
            variant={isWithdrawal ? "destructive" : "default"}
          >
            {isSubmitting ? "Salvando..." : "Confirmar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
